import React, { useState, useEffect, useRef, useCallback } from 'react';
import Check from 'lucide-react/dist/esm/icons/check.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Lock from 'lucide-react/dist/esm/icons/lock.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import useIsMobile from '../hooks/useIsMobile';
import { getGridDimensions } from '../utils/gridUtils';
import { squareStatusClass } from '../config/squarePalette';
import { parseUtcIso } from '../utils/parseUtcIso';
import SquareLegend from './SquareLegend';

// Deterministic color palette from initials
const INITIALS_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-green-600',
  'bg-teal-500',
  'bg-blue-600',
  'bg-indigo-500',
  'bg-purple-600',
];

function getInitialsColor(initials) {
  if (!initials) return INITIALS_COLORS[0];
  return INITIALS_COLORS[initials.charCodeAt(0) % 8];
}

function CountdownTimer({ heldUntil }) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const target = parseUtcIso(heldUntil);
    if (!target) {
      setSeconds(0);
      return undefined;
    }
    const update = () => {
      const remaining = Math.max(0, Math.floor((target.getTime() - Date.now()) / 1000));
      setSeconds(remaining);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [heldUntil]);

  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return (
    <span className="font-mono tabular-nums">
      {m}:{String(s).padStart(2, '0')}
    </span>
  );
}

/**
 * SquareCell — memoized per-cell renderer.
 *
 * Wrapped in React.memo so poll-driven squares[] updates don't re-render
 * cells whose status is unchanged. The parent must hand down stable
 * references (memoized onSelect, same-status sq objects) for this to hold.
 */
const SquareCell = React.memo(function SquareCell({
  num,
  sq,
  isSelected,
  isWinning,
  isLocking,
  isMobile,
  readOnly,
  highlightAvailable,
  onSelect,
  isStandard,
}) {
  const paletteKey = isWinning
    ? 'winner'
    : isSelected
    ? 'selected'
    : (sq.status === 'taken' || sq.status === 'sold')
    ? sq.status
    : sq.status === 'held'
    ? 'held'
    : 'available';
  const palette = squareStatusClass(paletteKey);

  let cellClass;
  if (isStandard) {
    // Match Standard layout's exact cell classes (SquareGameStyleStandard.jsx line 464)
    cellClass =
      'aspect-square rounded border flex items-center justify-center text-xs font-medium transition-all relative ' +
      palette.cell + ' ';
    if (isWinning) {
      cellClass += 'scale-110 z-10 ';
    } else if (isSelected) {
      cellClass += 'cursor-pointer ';
    } else if (sq.status === 'taken' || sq.status === 'sold' || sq.status === 'held') {
      cellClass += 'cursor-not-allowed ';
    } else {
      cellClass +=
        palette.hover + ' cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ';
    }
  } else {
    cellClass =
      'relative aspect-square text-[10px] sm:text-xs md:text-sm rounded transition-all duration-150 ' +
      (isMobile ? 'border ' : 'border-2 min-h-[44px] min-w-[44px] ') +
      palette.cell + ' ';
    if (isWinning) {
      cellClass += 'shadow-2xl shadow-amber-500/60 scale-110 z-20 cursor-default ';
    } else if (isLocking) {
      cellClass += 'scale-110 ';
    } else if (isSelected) {
      cellClass += 'ring-offset-1 dark:ring-offset-gray-900 animate-pulse cursor-pointer ';
    } else if (sq.status === 'taken' || sq.status === 'sold') {
      cellClass += 'cursor-not-allowed ';
    } else if (sq.status === 'held') {
      cellClass += 'cursor-not-allowed ';
    } else {
      cellClass +=
        palette.hover + ' hover:scale-110 hover:shadow-lg hover:z-10 cursor-pointer ' +
        'active:scale-95 ';
      if (highlightAvailable) {
        cellClass += 'ring-2 ring-emerald-400 ring-offset-1 animate-pulse ';
      }
    }
  }

  const handleClick = () => {
    if (readOnly || sq.status !== 'available') return;
    onSelect?.(sq.number);
  };

  return (
    <button
      aria-label={`Square ${num}, ${sq.status}`}
      title={sq.status === 'available' ? `Click to claim #${num}` : undefined}
      disabled={readOnly || (sq.status !== 'available' && !isSelected)}
      onClick={handleClick}
      className={cellClass}
      style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Square number — centered in standard, top-left in showcase */}
      {isStandard ? (
        <span>{num}</span>
      ) : (
        <span className={`absolute top-0 left-0.5 sm:top-1 sm:left-1 text-[10px] sm:text-xs md:text-sm font-bold leading-none ${isWinning ? 'opacity-90 text-white' : 'opacity-70'}`}>{num}</span>
      )}

      {/* Winning square: trophy icon — centered */}
      {isWinning && (
        <Trophy
          size={isMobile ? 12 : 16}
          className="absolute inset-0 m-auto text-white drop-shadow-lg"
          strokeWidth={2.5}
        />
      )}

      {/* Taken: initials badge — showcase only */}
      {!isStandard && (sq.status === 'taken' || sq.status === 'sold') && sq.owner && !isLocking && !isWinning && (
        <span
          className={`absolute bottom-0 right-0 sm:bottom-1 sm:right-1 w-3 h-3 sm:w-4 sm:h-4 rounded-full flex items-center justify-center text-white text-[7px] sm:text-[8px] font-bold ${getInitialsColor(sq.owner.initials)}`}
        >
          {sq.owner.initials}
        </span>
      )}

      {/* Selected: checkmark */}
      {isSelected && !isLocking && !isWinning && (
        <Check
          className="absolute top-0 right-0 sm:top-0.5 sm:right-0.5 text-white"
          size={isMobile ? 7 : 8}
          strokeWidth={3}
        />
      )}

      {/* Held: clock icon always shown; countdown text in showcase only */}
      {sq.status === 'held' && !isLocking && !isWinning && (
        <>
          {!isStandard && sq.heldUntil && (
            <span className="absolute inset-x-0 bottom-0 hidden sm:flex items-center justify-center text-[10px] sm:text-xs bg-amber-400/20 px-1 rounded-b">
              <CountdownTimer heldUntil={sq.heldUntil} />
            </span>
          )}
          <Clock
            size={10}
            strokeWidth={2.5}
            className="absolute bottom-0 right-0 text-amber-400 drop-shadow"
          />
        </>
      )}

      {/* Locking animation: lock icon — showcase only */}
      {!isStandard && isLocking && (
        <Lock
          size={isMobile ? 10 : 12}
          className="absolute inset-0 m-auto text-gray-500 animate-ping"
        />
      )}
    </button>
  );
});

function SquareGrid({
  squares,
  selectedSquare,
  onSelect,
  readOnly = false,
  highlightAvailable = false,
  gridSize,
  winningSquare = null,
  variant = 'showcase',
}) {
  const isStandard = variant === 'standard';
  const isMobile = useIsMobile();

  // Track squares that just transitioned available→taken to show lock animation
  const [lockedSquares, setLockedSquares] = useState({});
  const prevSquaresRef = useRef({});

  useEffect(() => {
    if (!squares) return;
    const prev = prevSquaresRef.current;
    const newLocked = {};
    squares.forEach((sq) => {
      if (prev[sq.number] === 'available' && (sq.status === 'taken' || sq.status === 'sold')) {
        newLocked[sq.number] = true;
      }
    });
    if (Object.keys(newLocked).length > 0) {
      setLockedSquares((prev) => ({ ...prev, ...newLocked }));
      Object.keys(newLocked).forEach((num) => {
        setTimeout(() => {
          setLockedSquares((prev) => {
            const next = { ...prev };
            delete next[num];
            return next;
          });
        }, 1500);
      });
    }
    const map = {};
    squares.forEach((sq) => (map[sq.number] = sq.status));
    prevSquaresRef.current = map;
  }, [squares]);

  // Stable handler so memoized cells don't re-render when parent re-renders
  // with the same onSelect identity.
  const stableSelect = useCallback((n) => onSelect?.(n), [onSelect]);

  if (!squares || squares.length === 0) return null;

  const squareMap = {};
  squares.forEach((sq) => (squareMap[sq.number] = sq));

  // Calculate columns dynamically from gridSize prop, squares length, or default 10
  const totalSquares = gridSize || squares.length;
  const { cols, rows } = getGridDimensions(totalSquares);

  // Mobile: hide headers, tight gap, let cells fill available width
  // Desktop: show row/col headers, 44px min cells, 4px gap
  // Standard variant: no headers, gap-1 (4px), cells fill width regardless of mobile
  const gridStyle = isStandard
    ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '4px' }
    : isMobile
    ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: '2px' }
    : { gridTemplateColumns: `1.5rem repeat(${cols}, minmax(44px, 1fr))`, gap: '4px' };

  return (
    <div
      className="select-none w-full"
      style={{ touchAction: 'manipulation' }}
    >
      {/* Landscape mobile: constrain grid height so it doesn't overflow viewport */}
      <div
        className="w-full landscape-grid-scroll"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <style>{`
          @media (orientation: landscape) and (max-height: 500px) {
            .landscape-grid-scroll { max-height: 55vh; overflow-y: auto; }
          }
        `}</style>
        <div className="w-full grid" style={gridStyle}>
        {/* Column headers — hidden on mobile and in standard variant */}
        {!isStandard && !isMobile && (
          <>
            <div className="w-6" />
            {Array.from({ length: cols }, (_, c) => (
              <div
                key={`col-${c}`}
                className="flex items-center justify-center text-[10px] font-semibold text-gray-400 dark:text-gray-500 aspect-square"
              >
                {c}
              </div>
            ))}
          </>
        )}

        {/* Rows */}
        {Array.from({ length: rows }, (_, row) => (
          <React.Fragment key={`row-${row}`}>
            {/* Row header — hidden on mobile and in standard variant */}
            {!isStandard && !isMobile && (
              <div className="flex items-center justify-center text-[10px] font-semibold text-gray-400 dark:text-gray-500 w-6">
                {row}
              </div>
            )}
            {/* Squares in this row */}
            {Array.from({ length: cols }, (_, col) => {
              const num = row * cols + col + 1;
              if (num > totalSquares) return <div key={`empty-${num}`} />;
              const sq = squareMap[num];
              if (!sq) return <div key={num} />;

              const isLocking = !!lockedSquares[num];
              const isSelected = sq.status === 'selected' || selectedSquare === num;
              const isWinning = winningSquare === num;

              return (
                <SquareCell
                  key={num}
                  num={num}
                  sq={sq}
                  isSelected={isSelected}
                  isWinning={isWinning}
                  isLocking={isLocking}
                  isMobile={isMobile}
                  readOnly={readOnly}
                  highlightAvailable={highlightAvailable}
                  onSelect={stableSelect}
                  isStandard={isStandard}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
      </div>

      {/* Headers explanation — hidden on mobile and in standard variant */}
      {!isStandard && !isMobile && (
        <p className="mt-2 text-[10px] text-gray-400 dark:text-gray-500 text-center">
          Row + Column = your lucky number combination
        </p>
      )}

      {/* Legend */}
      <SquareLegend showWinner={winningSquare !== null} />
    </div>
  );
}

export default React.memo(SquareGrid);
